import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Form, Select, Input, Button, message } from "antd";
import { useClients } from "../../queries/clients";
import { useProjects } from "../../queries/projects";
import { useCreateTicket } from "../../queries/tickets";
import { ProjectProps, ClientProps } from "../../types/interfaces";
import { ApiError } from "../../hooks/apiRequest";
import { PageShell } from "../../components/ui/PageShell";

/** A single-purpose form: the detail container, one column, no card chrome —
 *  a form page shouldn't look like a list page wearing a form. */
export const TicketNewPage = () => {
  const navigate = useNavigate();
  const { data: clients } = useClients();
  const [clientId, setClientId] = useState<string | undefined>();
  const { data: projects } = useProjects({ clientId });
  const createTicket = useCreateTicket();
  const [form] = Form.useForm();

  const handleSubmit = async () => {
    const values = await form.validateFields();
    try {
      const ticket = await createTicket.mutateAsync(values);
      navigate(`/support/tickets/${ticket._id}`);
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : "Could not create the ticket");
    }
  };

  return (
    <PageShell
      kind="detail"
      crumbs={[{ label: "Support", to: "/support" }, { label: "New ticket" }]}
      title="Raise a ticket"
      subtitle="Tell us which account and project this concerns and give it a subject. You can add detail in the thread once it's open."
    >
      <div
        style={{
          maxWidth: 520,
          padding: 20,
          background: "var(--q-surface)",
          border: "1px solid var(--q-border)",
          borderRadius: "var(--q-radius)",
        }}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="clientId" label="Client" rules={[{ required: true }]}>
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Which account is this for?"
              options={(clients || []).map((c: ClientProps) => ({ label: c.name, value: c._id }))}
              onChange={(value) => {
                setClientId(value);
                form.setFieldValue("projectId", undefined);
              }}
            />
          </Form.Item>
          <Form.Item name="projectId" label="Project" rules={[{ required: true }]}>
            <Select
              disabled={!clientId}
              placeholder={clientId ? "Pick a project" : "Pick a client first"}
              options={(projects || []).map((p: ProjectProps) => ({ label: p.name, value: p._id }))}
            />
          </Form.Item>
          <Form.Item name="subject" label="Subject" rules={[{ required: true }]}>
            <Input placeholder="One line — what's wrong, or what do you need?" />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <span style={{ display: "flex", gap: 8 }}>
              <Button type="primary" onClick={handleSubmit} loading={createTicket.isPending}>
                Create ticket
              </Button>
              <Button onClick={() => navigate("/support")}>Cancel</Button>
            </span>
          </Form.Item>
        </Form>
      </div>
    </PageShell>
  );
};
